import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {
  GetTaskAssignmentListRequestV2,
  TaskAssignmentSimpleResponse,
  TaskAssignmentStateEnum} from 'src/api/ProactApiClient';
import {
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {ViewActionIcon} from 'src/design/molecules/ViewActionIcon';
import {Pill} from 'src/design/atoms/Pill';
import {Spinner} from 'src/design/atoms/Spinner';
import {TaskAssignmentStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TaskAssignmentPillConfig} from 'src/app/tasks/taskHelpers';
import {useAppSelector} from 'src/redux/hooks';
import {useGetTaskAssignmentListV2Query} from 'src/redux/queries/taskAssignment';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface TaskAssignmentsTableProps {
  apiParams?: Partial<GetTaskAssignmentListRequestV2>;
  taskType?: TaskTypeEnum;
  defaultStates?: TaskAssignmentStateEnum[];
  showForUserId?: number;
  enableRefresh?: boolean;
}

export const TaskAssignmentsTable: React.FC<TaskAssignmentsTableProps> = ({
  apiParams,
  taskType,
  defaultStates,
  showForUserId,
  enableRefresh = true,
}) => {
  const navigate = useAppNavigate();
  const {session} = useAppSelector(state => state.session);
  const userId = showForUserId ?? session?.user?.id;

  const columns = useMemo<MRT_ColumnDef<TaskAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnActions: false,
        size: 100,
        enableResizing: false,
        enableClickToCopy: false,
        Cell: ({row}) => (
          <Group gap="xs">
            <ViewActionIcon
              onClick={() => {
                if (showForUserId === undefined) {
                  navigate(
                    PathUtility.ViewTaskAssignment(
                      row.original.taskDefinition?.taskType,
                      String(row.original.id)
                    )
                  );
                } else {
                  navigate(
                    PathUtility.TaskView(
                      row.original.taskDefinition?.taskType,
                      String(row.original.taskId),
                      'content'
                    )
                  );
                }
              }}
              tooltipText="View Assignment"
              size="sm"
            />
          </Group>
        ),
      },
      {
        accessorKey: 'taskDefinition.subject',
        header: 'Subject',
        size: 500,
      },
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      ...(taskType === TaskTypeEnum.Approval || taskType === TaskTypeEnum.Actions
        ? [
            {
              accessorKey: 'taskDefinition.sourceFormIdentifier',
              header: 'Source Form',
            },
            {
              accessorKey: 'taskDefinition.sourceFormDescription',
              header: 'Source Form Description',
            },
          ]
        : []),
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: TaskAssignmentStateDataOptions,
        },
        Cell: ({row}) => {
          const pills = TaskAssignmentPillConfig(
            row.original.state,
            row.original.taskDefinition.taskType,
            row.original.taskDefinition.state,
            row.original.dueAt
          );
          return pills.length > 0 ? <Pill {...pills[0]} /> : null;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Assigned Date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        accessorKey: 'completedAt',
        header: 'Completed Date',
        Cell: ({cell}) => (
          <>
            {cell.getValue<Date | null>() &&
              cell.getValue<Date | null>()?.toString().toShortDate()}
          </>
        ),
      },
      {
        accessorKey: 'dueAt',
        header: 'Due Date',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | undefined | null>();
          if (value && new Date(value) < new Date()) {
            return <Box c="red">{value?.toString().toShortDate()}</Box>;
          } else {
            return <>{value && value?.toString().toShortDate()}</>;
          }
        },
      },
    ],
    [navigate, showForUserId, taskType]
  );

  const tableState = useTableState({
    identifier: 'task-assignments',
    columns: columns,
    defaultSorting: [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const apiRequest: GetTaskAssignmentListRequestV2 = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    taskType: taskType,
    assignedToUserId: userId,
    assignmentStates: defaultStates,
    ...apiParams,
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetTaskAssignmentListV2Query(apiRequest);
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowSelection: false,
      enableRowActions: false,
      columns: columns,
      data: rows,
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: enableRefresh ? refetch : undefined,
  });

  return !isLoading ? <>{table}</> : <Spinner loadingText="Loading...." />;
};
